/**
 * SpaceEnemyBase.ts
 * Abstract pooled base class for World 2 space enemies.
 *
 * Provides the shared state (spawn ID, kind tag, HP, active flag) and the
 * `takeDamage()` rule, which is identical across enemy types in this slice:
 * any positive damage decrements HP, and HP reaching 0 marks the enemy dead
 * + inactive so `spaceEnemySystem` reaps it on the next tick.
 *
 * Subclasses must implement `update()` and `spawn()`; movement and firing
 * patterns are entirely owned by the subclass so this base never assumes
 * a movement model. The `kind` discriminator lets the renderer pick the
 * right colour / sprite without `instanceof` checks.
 *
 * NO React imports, NO Skia imports, NO async — engine-internal only.
 *
 * Phase 4 — World 2: Starfield Drift.
 *
 * @module SpaceEnemyBase
 */

import { BaseEntity } from './BaseEntity';
import type { LevelRuntime } from '../engine/gameState';
import type { SpaceEnemyKind } from '../../constants/spaceEnemyConstants';

/** Pooled space-enemy base. Subclasses fill in `kind`, `spawn()`, and `update()`. */
export abstract class SpaceEnemyBase extends BaseEntity {
  /** Monotonic ID counter shared across ALL enemy subclasses for stable React keys. */
  private static nextSpawnId = 1;

  /** Re-issued on every spawn so the renderer gets a stable key per "use". */
  public spawnId = 0;

  /** Discriminator picked up by the renderer to choose colour / sprite. */
  public abstract readonly kind: SpaceEnemyKind;

  /** Remaining HP. Zero or below marks the enemy dead. */
  public hp = 0;

  /** True while this enemy is in the active list. */
  public active = false;

  constructor(width: number, height: number) {
    super(0, 0, width, height);
    this.dead = true;
  }

  /**
   * Issue a fresh `spawnId` and mark the entity live. Subclasses call this
   * from their own `spawn()` after setting position / velocity / HP, so the
   * base class owns the ID lifecycle in one place.
   *
   * @internal Use from subclass `spawn()` only — not part of the public API.
   */
  protected markSpawned(): void {
    this.spawnId = SpaceEnemyBase.nextSpawnId++;
    this.dead = false;
    this.active = true;
  }

  /**
   * Pool-recycle hook. Subclasses override to also reset their movement
   * state (e.g. velocity, firing timers) but should call `super.reset()`
   * so the base flags stay coherent.
   */
  public reset(): void {
    this.x = 0;
    this.y = 0;
    this.vx = 0;
    this.vy = 0;
    this.hp = 0;
    this.dead = true;
    this.active = false;
  }

  /**
   * Apply `amount` damage. Marks the enemy dead when HP reaches 0 so
   * `spaceEnemySystem.updateEnemies()` reaps it next tick.
   *
   * @param amount - Damage in HP units. Non-positive values are ignored
   *                 so a buggy caller can't HEAL the enemy by passing a
   *                 negative number (mirrors AsteroidEntity.takeDamage).
   * @param hitX   - Optional world-space X of the projectile impact.
   *                 Default base impl ignores it — subclasses with a
   *                 directional shield (e.g. {@link ShieldSatellite})
   *                 override to gate damage on impact direction. Mirrors
   *                 the {@link VoidMothership.takeDamage} signature so
   *                 every space-hazard `takeDamage` accepts the same
   *                 shape.
   * @param hitY   - Optional world-space Y. Must accompany `hitX`.
   * @returns `true` if the enemy was killed by this hit.
   */
  public takeDamage(amount: number, hitX?: number, hitY?: number): boolean {
    void hitX;
    void hitY;
    if (this.dead || amount <= 0) return false;
    this.hp -= amount;
    if (this.hp <= 0) {
      this.hp = 0;
      this.dead = true;
      this.active = false;
      return true;
    }
    return false;
  }

  /**
   * Advance one fixed timestep. Subclasses implement movement, AI, and
   * culling here.
   *
   * @param dt    - Fixed timestep in seconds.
   * @param level - Level runtime (unused by space enemies — kept to satisfy
   *                the `BaseEntity` contract).
   */
  public abstract update(dt: number, level: LevelRuntime): void;

  /**
   * Initialise the entity at a world position. Subclasses set their own
   * velocity / HP / per-kind state, then call `markSpawned()` so the base
   * owns the spawn-ID lifecycle in exactly one place.
   *
   * @param x - Spawn X (world space, top-left of hitbox).
   * @param y - Spawn Y (world space, top-left of hitbox).
   */
  public abstract spawn(x: number, y: number): void;
}
